import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Plus,
  Minus,
  ChevronRight,
  Search,
  Loader2,
  Building,
  CheckCircle,
  AlertCircle,
  Clock,
  XCircle,
  FileText,
  Shield,
  Filter,
  GraduationCap,
  ShieldCheck,
  Check,
  Eye,
  Download,
  History,
  ExternalLink
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import ProfileQuickQuotePopup from '@/components/modals/ProfileQuickQuotePopup';
import { RoleTemplateDialog } from '@/pages/qualifications/components/RoleTemplateDialog';
import { gcsGetSignedReadUrl } from '@/lib/gcsStorage';
import { Separator } from '@/components/ui/separator';
import { ScrollArea as SheetScrollArea } from '@/components/ui/scroll-area';

// Status configuration for COMPLIANCE display (not renewal status)
const STATUS_CONFIG = {
  valid: { label: 'Valid', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  expiring_soon: { label: 'Expiring', color: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: AlertCircle },
  expired: { label: 'Expired', color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
  missing: { label: 'Missing', color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
};

// Get COMPLIANCE status from assignment data
// Returns: valid, expiring_soon, expired, or missing
// This is separate from renewal status (quoting, booked, etc.)
const getComplianceStatus = (assignment) => {
  if (!assignment) return 'missing';

  const now = new Date();
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  // Check if has a completed qualification with expiry date
  if (assignment.expiry_date) {
    const expiryDate = new Date(assignment.expiry_date);

    if (expiryDate <= now) {
      return 'expired';
    }

    if (expiryDate <= thirtyDaysFromNow) {
      return 'expiring_soon';
    }

    // Has valid future expiry - check if qualification is actually completed
    if (assignment.status === 'completed' || assignment.completed_date) {
      return 'valid';
    }
  }

  // Check if has a completed qualification without expiry (e.g., one-time certification)
  if (assignment.status === 'completed' || assignment.completed_date) {
    return 'valid';
  }

  // No completed qualification - it's missing (regardless of quoting/booked status)
  return 'missing';
};

// Get RENEWAL status from assignment data
// Returns: quoting, quoted, booked, or null
const getRenewalStatus = (assignment) => {
  if (!assignment) return null;

  const status = assignment.status;
  if (status === 'quoting' || status === 'quoted' || status === 'booked' || status === 'in_progress') {
    return status === 'in_progress' ? 'booked' : status;
  }

  return null;
};

// Format date for display
const formatExpiryDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Get days until expiry
const getDaysUntilExpiry = (dateString) => {
  if (!dateString) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const expiry = new Date(dateString);
  expiry.setHours(0, 0, 0, 0);
  const diffTime = expiry - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Requirement row component for assigned panel
const AssignedRequirementRow = ({ requirement, onUnassign, onRequestQuote, onViewCertificate, disabled, roleTemplateMap }) => {
  const complianceStatus = getComplianceStatus(requirement);
  const renewalStatus = getRenewalStatus(requirement);
  const statusConfig = STATUS_CONFIG[complianceStatus] || STATUS_CONFIG.missing;
  const StatusIcon = statusConfig.icon;
  const daysUntilExpiry = getDaysUntilExpiry(requirement.expiry_date);

  // Show Quote button only if:
  // - Missing, expiring, or expired (needs renewal)
  // - AND not already quoting or booked
  const needsRenewal = ['missing', 'expiring_soon', 'expired'].includes(complianceStatus);
  const hasActiveQuoteOrBooking = ['quoting', 'quoted', 'booked'].includes(renewalStatus);
  const showQuoteButton = needsRenewal && !hasActiveQuoteOrBooking;

  // Get renewal status label for display
  const getRenewalLabel = () => {
    switch (renewalStatus) {
      case 'quoting': return 'Quote Requested';
      case 'quoted': return 'Quote Provided';
      case 'booked': return 'Training Booked';
      default: return null;
    }
  };
  const renewalLabel = getRenewalLabel();

  return (
    <div className="flex items-center gap-2 px-3 py-2 transition-colors border-b last:border-0 group hover:bg-gray-50">
      {/* Unassign button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={disabled ? undefined : () => onUnassign(requirement)}
              disabled={disabled}
              className={cn(
                'shrink-0 p-1 rounded transition-colors',
                disabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-red-100 text-gray-400 hover:text-red-600'
              )}
            >
              <Minus className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Unassign requirement</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Requirement info - clickable to view certificate */}
      <div
        className={cn(
          "flex-1 min-w-0",
          (requirement.evidence_url || requirement.completed_date) && "cursor-pointer"
        )}
        onClick={() => onViewCertificate?.(requirement)}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">{requirement.name}</span>
          <Badge
            variant="outline"
            className={cn('text-[10px] py-0 px-1.5 shrink-0', statusConfig.color)}
          >
            <StatusIcon className="h-2.5 w-2.5 mr-0.5" />
            {statusConfig.label}
          </Badge>
          {/* Show renewal status badge if active */}
          {renewalLabel && (
            <Badge
              variant="outline"
              className="text-[10px] py-0 px-1.5 shrink-0 bg-purple-50 text-purple-700 border-purple-200"
            >
              <FileText className="h-2.5 w-2.5 mr-0.5" />
              {renewalLabel}
            </Badge>
          )}
          {/* Certificate indicator */}
          {requirement.evidence_url && (
            <Eye className="h-3 w-3 text-gray-400 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span className="text-gray-500">
            {requirement.category || 'General'}
          </span>
          {/* Role template source badges */}
          {requirement.role_template_ids && requirement.role_template_ids.length > 0 ? (
            requirement.role_template_ids.map(templateId => {
              const tmpl = roleTemplateMap?.get(templateId);
              if (!tmpl) return null;
              const badge = (
                <Badge
                  variant="outline"
                  className={cn(
                    "text-[10px] py-0 px-1 shrink-0",
                    tmpl.job_profile && "cursor-pointer"
                  )}
                  style={{
                    backgroundColor: (tmpl.color || '#6366f1') + '15',
                    borderColor: (tmpl.color || '#6366f1') + '40',
                    color: tmpl.color || '#6366f1'
                  }}
                >
                  {tmpl.name}
                </Badge>
              );
              if (tmpl.job_profile) {
                return (
                  <Popover key={templateId}>
                    <PopoverTrigger asChild>
                      {badge}
                    </PopoverTrigger>
                    <PopoverContent className="w-80 max-h-64 overflow-y-auto" align="start">
                      <div className="space-y-2">
                        <div className="font-medium text-sm">{tmpl.name} — Job Profile</div>
                        <div
                          className="prose prose-sm text-xs text-muted-foreground"
                          dangerouslySetInnerHTML={{ __html: tmpl.job_profile }}
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                );
              }
              return <span key={templateId}>{badge}</span>;
            })
          ) : (
            <Badge
              variant="outline"
              className="text-[10px] py-0 px-1 shrink-0 text-gray-400 border-gray-200"
            >
              Manual
            </Badge>
          )}
          {requirement.expiry_date && (
            <>
              <span className="text-gray-300">•</span>
              <span className={cn(
                complianceStatus === 'expired' ? 'text-red-600 font-medium' :
                complianceStatus === 'expiring_soon' ? 'text-yellow-600 font-medium' :
                'text-gray-500'
              )}>
                {complianceStatus === 'expired' ? 'Expired ' : 'Expires '}
                {formatExpiryDate(requirement.expiry_date)}
                {daysUntilExpiry !== null && complianceStatus === 'expiring_soon' && (
                  <span className="ml-1">({daysUntilExpiry} days)</span>
                )}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Quote button - only show if needs renewal and no active quote/booking */}
      {showQuoteButton && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onRequestQuote(requirement)}
          className="h-7 text-xs shrink-0"
        >
          Quote
        </Button>
      )}
    </div>
  );
};

// Requirement row component for available panel
const AvailableRequirementRow = ({ requirement, onAssign, disabled }) => {
  return (
    <div className="flex items-center gap-2 px-3 py-2 transition-colors border-b last:border-0 group hover:bg-gray-50">
      {/* Assign button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={disabled ? undefined : () => onAssign(requirement)}
              disabled={disabled}
              className={cn(
                'shrink-0 p-1 rounded transition-colors',
                disabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-green-100 text-gray-400 hover:text-green-600'
              )}
            >
              <Plus className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Assign requirement</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Requirement info */}
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium truncate block">{requirement.name}</span>
        <span className="text-xs text-gray-500">
          {requirement.category || 'General'}
        </span>
      </div>
      {requirement.legal_requirement && (
        <Badge variant="destructive" className="text-[9px] px-1 py-0 h-3.5 shrink-0">
          Legal
        </Badge>
      )}
    </div>
  );
};

// Calculate space compliance stats from requirements
// Uses compliance status for progress bar, renewal status for additional stats
const calculateSpaceStats = (requirements) => {
  const total = requirements.length;
  if (total === 0) return { total: 0, valid: 0, expiring: 0, expired: 0, missing: 0, booked: 0, quoting: 0, percentage: 0 };

  let valid = 0, expiring = 0, expired = 0, missing = 0;
  let booked = 0, quoting = 0; // Renewal statuses (additional stats)

  requirements.forEach(req => {
    // Get compliance status for progress bar
    const complianceStatus = getComplianceStatus(req);
    switch (complianceStatus) {
      case 'valid':
        valid++;
        break;
      case 'expiring_soon':
        expiring++;
        break;
      case 'expired':
        expired++;
        break;
      case 'missing':
      default:
        missing++;
        break;
    }

    // Get renewal status for additional stats (separate from compliance)
    const renewalStatus = getRenewalStatus(req);
    if (renewalStatus === 'booked') booked++;
    else if (renewalStatus === 'quoting' || renewalStatus === 'quoted') quoting++;
  });

  // Compliance = valid + expiring (they're still compliant until expired)
  const compliant = valid + expiring;
  const percentage = total > 0 ? Math.round((compliant / total) * 100) : 0;

  return { total, valid, expiring, expired, missing, booked, quoting, percentage };
};

// Section header with space name and optional compliance progress bar
const SpaceSection = ({ spaceName, count, requirements, children, isCollapsed, onToggle }) => {
  if (count === 0) return null;

  const showStats = requirements && requirements.length > 0;
  const stats = showStats ? calculateSpaceStats(requirements) : null;

  // Simple header without stats (for Available panel)
  const headerContent = (
    <div
      className="px-3 py-1.5 text-[11px] font-semibold text-gray-500 bg-gray-50 border-b sticky top-0 z-10 flex items-center gap-2 cursor-pointer hover:bg-gray-100 transition-colors"
      onClick={onToggle}
    >
      <Building className="h-3 w-3" />
      <span className="flex-1 truncate">{spaceName}</span>

      {/* Traffic light progress bar - only shown for assigned requirements */}
      {showStats && stats && (
        <div className="flex items-center gap-1.5">
          <div className="w-16 bg-gray-200 rounded-full h-1.5 overflow-hidden flex">
            {stats.total > 0 && (
              <>
                {stats.valid > 0 && (
                  <div className="h-1.5 bg-green-500" style={{ width: `${(stats.valid / stats.total) * 100}%` }} />
                )}
                {stats.expiring > 0 && (
                  <div className="h-1.5 bg-amber-500" style={{ width: `${(stats.expiring / stats.total) * 100}%` }} />
                )}
                {stats.expired > 0 && (
                  <div className="h-1.5 bg-red-500" style={{ width: `${(stats.expired / stats.total) * 100}%` }} />
                )}
                {stats.missing > 0 && (
                  <div className="h-1.5 bg-red-400" style={{ width: `${(stats.missing / stats.total) * 100}%` }} />
                )}
              </>
            )}
          </div>
          <span className={cn(
            'text-[10px] font-bold min-w-[28px] text-right',
            stats.percentage >= 80 ? 'text-green-600' :
            stats.percentage >= 50 ? 'text-amber-600' :
            'text-red-600'
          )}>
            {stats.percentage}%
          </span>
        </div>
      )}

      {/* Show count badge for Available panel */}
      {!showStats && (
        <span className="text-[10px] text-gray-400">{count}</span>
      )}

      <ChevronRight className={cn('h-3 w-3 transition-transform', !isCollapsed && 'rotate-90')} />
    </div>
  );

  return (
    <div className="mb-2">
      {showStats && stats ? (
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              {headerContent}
            </TooltipTrigger>
            <TooltipContent side="bottom" className="p-3 bg-white border shadow-lg" sideOffset={5}>
              <div className="font-semibold text-sm text-gray-900 mb-2 pb-2 border-b">{spaceName}</div>

              {/* Compliance percentage */}
              <div className={cn(
                'rounded-lg p-2 mb-2',
                stats.percentage >= 80 ? 'bg-green-50 border border-green-200' :
                stats.percentage >= 50 ? 'bg-yellow-50 border border-yellow-200' :
                'bg-red-50 border border-red-200'
              )}>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-xs font-medium text-gray-600">Compliance</span>
                  <span className={cn(
                    'text-lg font-bold',
                    stats.percentage >= 80 ? 'text-green-600' :
                    stats.percentage >= 50 ? 'text-yellow-600' :
                    'text-red-600'
                  )}>
                    {stats.percentage}%
                  </span>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-1.5 text-xs">
                <div className="bg-green-50 rounded px-2 py-1 border border-green-200 flex justify-between">
                  <span className="text-green-700">Valid</span>
                  <span className="font-bold text-green-600">{stats.valid}</span>
                </div>
                <div className="bg-yellow-50 rounded px-2 py-1 border border-yellow-200 flex justify-between">
                  <span className="text-yellow-700">Expiring</span>
                  <span className="font-bold text-yellow-600">{stats.expiring}</span>
                </div>
                <div className="bg-red-50 rounded px-2 py-1 border border-red-200 flex justify-between">
                  <span className="text-red-700">Expired</span>
                  <span className="font-bold text-red-600">{stats.expired}</span>
                </div>
                <div className="bg-red-50 rounded px-2 py-1 border border-red-200 flex justify-between">
                  <span className="text-red-700">Missing</span>
                  <span className="font-bold text-red-600">{stats.missing}</span>
                </div>
                <div className="bg-blue-50 rounded px-2 py-1 border border-blue-200 flex justify-between">
                  <span className="text-blue-700">Booked</span>
                  <span className="font-bold text-blue-600">{stats.booked}</span>
                </div>
                <div className="bg-purple-50 rounded px-2 py-1 border border-purple-200 flex justify-between">
                  <span className="text-purple-700">Quoting</span>
                  <span className="font-bold text-purple-600">{stats.quoting}</span>
                </div>
              </div>

              <div className="mt-2 pt-2 border-t text-xs flex justify-between text-gray-600">
                <span>Total Assigned</span>
                <span className="font-bold text-gray-900">{stats.total}</span>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        headerContent
      )}
      {!isCollapsed && children}
    </div>
  );
};

// Certificate detail sheet for viewing current certificate and history
const CertificateDetailSheet = ({ requirement, open, onOpenChange }) => {
  const [loadingUrl, setLoadingUrl] = useState(null);

  const complianceStatus = requirement ? getComplianceStatus(requirement) : 'missing';
  const statusConfig = STATUS_CONFIG[complianceStatus] || STATUS_CONFIG.missing;
  const StatusIcon = statusConfig.icon;

  const handleViewCertificate = async (evidenceUrl, key = 'current') => {
    if (!evidenceUrl) return;
    setLoadingUrl(key);
    try {
      const signedUrl = await gcsGetSignedReadUrl(evidenceUrl);
      window.open(signedUrl, '_blank');
    } catch (error) {
      console.error('Error getting signed URL:', error);
      toast.error('Failed to load certificate');
    } finally {
      setLoadingUrl(null);
    }
  };

  const history = requirement?.evidence_version_history || [];
  // Sort history newest first
  const sortedHistory = [...history].sort((a, b) => {
    const dateA = new Date(a.replaced_at || a.uploaded_at || 0);
    const dateB = new Date(b.replaced_at || b.uploaded_at || 0);
    return dateB - dateA;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle>{requirement?.name}</DialogTitle>
          <DialogDescription className="sr-only">Certificate details and history</DialogDescription>
        </DialogHeader>

        {requirement && (
          <SheetScrollArea className="flex-1 px-6 pb-6">
            <div className="space-y-6">
            {/* Status */}
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className={cn('text-xs py-0.5 px-2', statusConfig.color)}
              >
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusConfig.label}
              </Badge>
              {requirement.certificate_number && (
                <Badge variant="outline" className="text-xs py-0.5 px-2 text-gray-600">
                  #{requirement.certificate_number}
                </Badge>
              )}
            </div>

            {/* Current Certificate Details */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-900">Current Certificate</h4>

              {(requirement.evidence_url || requirement.completed_date) ? (
                <div className="rounded-lg border bg-gray-50/50 p-3 space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {requirement.completed_date && (
                      <div>
                        <span className="text-gray-500 text-xs">Completed</span>
                        <p className="font-medium">{formatDate(requirement.completed_date)}</p>
                      </div>
                    )}
                    {requirement.expiry_date && (
                      <div>
                        <span className="text-gray-500 text-xs">Expires</span>
                        <p className={cn(
                          'font-medium',
                          complianceStatus === 'expired' ? 'text-red-600' :
                          complianceStatus === 'expiring_soon' ? 'text-yellow-600' :
                          'text-gray-900'
                        )}>
                          {formatDate(requirement.expiry_date)}
                        </p>
                      </div>
                    )}
                    {requirement.certificate_number && (
                      <div>
                        <span className="text-gray-500 text-xs">Certificate No.</span>
                        <p className="font-medium">{requirement.certificate_number}</p>
                      </div>
                    )}
                    {requirement.category && (
                      <div>
                        <span className="text-gray-500 text-xs">Category</span>
                        <p className="font-medium">{requirement.category}</p>
                      </div>
                    )}
                  </div>

                  {requirement.notes && (
                    <div>
                      <span className="text-gray-500 text-xs">Notes</span>
                      <p className="text-sm text-gray-700 mt-0.5">{requirement.notes}</p>
                    </div>
                  )}

                  {(() => {
                    const evidenceFilesList = Array.isArray(requirement.evidence_files) && requirement.evidence_files.length > 0
                      ? requirement.evidence_files
                      : requirement.evidence_url
                        ? [{ url: requirement.evidence_url, file_name: 'Certificate', is_primary: true }]
                        : [];
                    if (evidenceFilesList.length === 0) return null;
                    if (evidenceFilesList.length === 1) {
                      return (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full gap-2"
                          onClick={() => handleViewCertificate(evidenceFilesList[0].url, 'current')}
                          disabled={loadingUrl === 'current'}
                        >
                          {loadingUrl === 'current' ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                          View Certificate
                        </Button>
                      );
                    }
                    return (
                      <div className="space-y-1">
                        {evidenceFilesList.map((ef, idx) => {
                          const docType = ef.doc_type || (ef.is_primary ? 'certificate' : 'other');
                          const typeLabel = docType === 'certificate' ? 'Certificate' : docType === 'card' ? 'ID / Card' : 'Supporting';
                          const typeColor = docType === 'certificate' ? 'text-blue-500' : docType === 'card' ? 'text-emerald-500' : 'text-gray-400';
                          return (
                            <Button
                              key={idx}
                              variant="outline"
                              size="sm"
                              className="w-full gap-2 justify-start"
                              onClick={() => handleViewCertificate(ef.url, `current-${idx}`)}
                              disabled={loadingUrl === `current-${idx}`}
                            >
                              {loadingUrl === `current-${idx}` ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                              <span className="truncate">{ef.file_name || `File ${idx + 1}`}</span>
                              <span className={`text-[10px] font-medium ml-auto ${typeColor}`}>{typeLabel}</span>
                            </Button>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed p-4 text-center text-gray-400">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No certificate uploaded</p>
                </div>
              )}
            </div>

            {/* Certificate History */}
            {sortedHistory.length > 0 && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <History className="h-4 w-4 text-gray-500" />
                    <h4 className="text-sm font-medium text-gray-900">Previous Certificates</h4>
                    <Badge variant="secondary" className="text-xs">{sortedHistory.length}</Badge>
                  </div>

                  <div className="space-y-2">
                    {sortedHistory.map((entry, index) => (
                      <div
                        key={index}
                        className="rounded-lg border bg-white p-3 flex items-center gap-3"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-sm">
                            <FileText className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                            <span className="text-gray-600 truncate">
                              {entry.url?.split('/').pop()?.replace(/^\d+_/, '') || 'Certificate'}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                            {entry.uploaded_at && (
                              <span>Issued: {formatDate(entry.uploaded_at)}</span>
                            )}
                            {entry.replaced_at && (
                              <span>Replaced: {formatDate(entry.replaced_at)}</span>
                            )}
                          </div>
                        </div>
                        {entry.url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 shrink-0"
                            onClick={() => handleViewCertificate(entry.url, `history-${index}`)}
                            disabled={loadingUrl === `history-${index}`}
                          >
                            {loadingUrl === `history-${index}` ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <ExternalLink className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
            </div>
          </SheetScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};

export function ProfileQualificationsManager({ userId, userProfile }) {
  const { userProfile: currentUser, company, canEditTraining } = useAuth();
  const hasEditPermission = canEditTraining();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userSpaces, setUserSpaces] = useState([]);
  const [allRequirements, setAllRequirements] = useState({}); // Grouped by space
  const [assignedRequirements, setAssignedRequirements] = useState({}); // Grouped by space
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpaceFilter, setSelectedSpaceFilter] = useState('all');
  const [collapsedSections, setCollapsedSections] = useState({});

  // Quick Quote popup state
  const [quickQuoteOpen, setQuickQuoteOpen] = useState(false);
  const [quickQuoteRequirement, setQuickQuoteRequirement] = useState(null);

  // Certificate detail sheet state
  const [certDetailOpen, setCertDetailOpen] = useState(false);
  const [certDetailRequirement, setCertDetailRequirement] = useState(null);

  // Role template state
  const [roleTemplates, setRoleTemplates] = useState([]);
  const [roleTemplateMap, setRoleTemplateMap] = useState(new Map());
  const [applyRoleDialogOpen, setApplyRoleDialogOpen] = useState(false);
  const [applyingRoleId, setApplyingRoleId] = useState(null);
  const [roleSearchQuery, setRoleSearchQuery] = useState('');
  const [createRoleDialogOpen, setCreateRoleDialogOpen] = useState(false);
  const [userAppliedTemplateIds, setUserAppliedTemplateIds] = useState([]);

  // Pending operations queue for optimistic updates
  const pendingOperationsRef = useRef(new Map());

  // Get initial space filter from URL - only set once on mount
  const initialSpaceIdRef = useRef(searchParams.get('spaceId'));

  useEffect(() => {
    if (initialSpaceIdRef.current && selectedSpaceFilter === 'all') {
      setSelectedSpaceFilter(initialSpaceIdRef.current);
      initialSpaceIdRef.current = null; // Only run once
    }
  }, [selectedSpaceFilter]);

  // Load data
  const loadData = useCallback(async () => {
    if (!userId || !company?.id) return;

    setLoading(true);
    try {
      // 1. Get all spaces the user is a member of
      const { data: spaceMemberships, error: spacesError } = await supabase
        .from('space_members')
        .select(`
          id,
          project_id,
          projects (
            id,
            name,
            company_id
          )
        `)
        .eq('user_id', userId);

      if (spacesError) throw spacesError;

      const spaces = (spaceMemberships || [])
        .filter(sm => sm.projects && sm.projects.company_id === company.id)
        .map(sm => ({
          id: sm.projects.id,
          name: sm.projects.name,
          spaceMemberId: sm.id
        }));

      setUserSpaces(spaces);

      if (spaces.length === 0) {
        setAllRequirements({});
        setAssignedRequirements({});
        setLoading(false);
        return;
      }

      const spaceIds = spaces.map(s => s.id);

      // 2. Get all requirements for these spaces
      const { data: requirements, error: reqError } = await supabase
        .from('compliance_requirements')
        .select('*')
        .in('project_id', spaceIds)
        .eq('show_on_matrix', true)
        .order('name');

      if (reqError) throw reqError;

      // Group requirements by space
      const reqBySpace = {};
      spaces.forEach(space => {
        reqBySpace[space.id] = (requirements || []).filter(r => r.project_id === space.id);
      });
      setAllRequirements(reqBySpace);

      // 3. Get user's current assignments (only those marked as assigned = true)
      const { data: assignments, error: assignError } = await supabase
        .from('compliance_assignments')
        .select(`
          *,
          compliance_requirements (
            id,
            name,
            category,
            priority,
            renewal_period,
            legal_requirement
          )
        `)
        .eq('team_member_id', userId)
        .eq('assigned', true)
        .in('project_id', spaceIds);

      if (assignError) throw assignError;

      // Also get user_qualifications for status/evidence info
      const requirementIds = (requirements || []).map(r => r.id);
      const { data: userQuals, error: qualsError } = await supabase
        .from('user_qualifications')
        .select('*')
        .eq('user_id', userId)
        .in('requirement_id', requirementIds);

      if (qualsError) console.error('Error loading user qualifications:', qualsError);

      // Create a map for quick lookup of user quals
      const qualsMap = new Map();
      (userQuals || []).forEach(q => {
        qualsMap.set(q.requirement_id, q);
      });

      // Group assignments by space with merged status from user_qualifications
      const assignedBySpace = {};
      spaces.forEach(space => {
        assignedBySpace[space.id] = [];
      });

      (assignments || []).forEach(a => {
        if (a.compliance_requirements) {
          const userQual = qualsMap.get(a.requirement_id);
          const mergedAssignment = {
            ...a,
            name: a.compliance_requirements.name,
            category: a.compliance_requirements.category,
            priority: a.compliance_requirements.priority,
            legal_requirement: a.compliance_requirements.legal_requirement,
            role_template_ids: a.role_template_ids || [],
            // Merge user qualification status if available
            status: userQual?.status || a.status,
            expiry_date: userQual?.expiry_date || a.expiry_date,
            completed_date: userQual?.completed_date || a.completed_date,
            evidence_url: userQual?.evidence_url || a.evidence_url,
            evidence_version_history: userQual?.evidence_version_history || [],
            certificate_number: userQual?.certificate_number || null,
            notes: userQual?.notes || null
          };

          if (assignedBySpace[a.project_id]) {
            assignedBySpace[a.project_id].push(mergedAssignment);
          }
        }
      });

      setAssignedRequirements(assignedBySpace);

      // Load role templates for the company
      const { data: templates, error: templatesError } = await supabase
        .from('role_templates')
        .select('id, name, color, job_profile')
        .eq('company_id', company.id)
        .eq('is_active', true);

      if (!templatesError && templates) {
        setRoleTemplates(templates);
        const map = new Map();
        templates.forEach(t => map.set(t.id, t));
        setRoleTemplateMap(map);
      }

      // Load this user's applied role templates
      const { data: userTemplateLinks } = await supabase
        .from('user_role_templates')
        .select('role_template_id')
        .eq('user_id', userId);

      setUserAppliedTemplateIds((userTemplateLinks || []).map(l => l.role_template_id));

    } catch (error) {
      console.error('Error loading qualifications data:', error);
      toast.error('Failed to load qualifications');
    } finally {
      setLoading(false);
    }
  }, [userId, company?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Calculate available requirements (not assigned)
  const availableRequirements = useMemo(() => {
    const available = {};

    Object.keys(allRequirements).forEach(spaceId => {
      const assigned = assignedRequirements[spaceId] || [];
      const assignedIds = new Set(assigned.map(a => a.requirement_id));

      available[spaceId] = (allRequirements[spaceId] || [])
        .filter(req => !assignedIds.has(req.id));
    });

    return available;
  }, [allRequirements, assignedRequirements]);

  // Filter requirements by search and space
  const filterRequirements = useCallback((reqs, spaceFilter) => {
    let filtered = { ...reqs };

    // Filter by space
    if (spaceFilter && spaceFilter !== 'all') {
      filtered = { [spaceFilter]: filtered[spaceFilter] || [] };
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      Object.keys(filtered).forEach(spaceId => {
        filtered[spaceId] = filtered[spaceId].filter(req =>
          req.name?.toLowerCase().includes(query) ||
          req.category?.toLowerCase().includes(query)
        );
      });
    }

    return filtered;
  }, [searchQuery]);

  const filteredAssigned = useMemo(() =>
    filterRequirements(assignedRequirements, selectedSpaceFilter),
    [assignedRequirements, selectedSpaceFilter, filterRequirements]
  );

  const filteredAvailable = useMemo(() =>
    filterRequirements(availableRequirements, selectedSpaceFilter),
    [availableRequirements, selectedSpaceFilter, filterRequirements]
  );

  // Count totals
  const assignedCount = useMemo(() =>
    Object.values(filteredAssigned).reduce((sum, arr) => sum + arr.length, 0),
    [filteredAssigned]
  );

  const availableCount = useMemo(() =>
    Object.values(filteredAvailable).reduce((sum, arr) => sum + arr.length, 0),
    [filteredAvailable]
  );

  // Handle view certificate - opens CertificateDetailSheet
  const handleViewCertificate = useCallback((requirement) => {
    setCertDetailRequirement(requirement);
    setCertDetailOpen(true);
  }, []);

  // Handle request quote - opens ProfileQuickQuotePopup
  const handleRequestQuote = useCallback((requirement) => {
    setQuickQuoteRequirement(requirement);
    setQuickQuoteOpen(true);
  }, []);

  // Handle applying a role template
  const handleApplyRoleTemplate = useCallback(async (template) => {
    if (!userId || !company?.id) return;

    setApplyingRoleId(template.id);
    try {
      // Get the template's courses with link IDs
      const { data: templateCourses, error: reqError } = await supabase
        .from('role_template_requirements')
        .select('course_name, course_link_id, course_link_type')
        .eq('role_template_id', template.id);

      if (reqError) throw reqError;

      // Always create the direct user-to-template link
      const { error: linkError } = await supabase
        .from('user_role_templates')
        .upsert({
          user_id: userId,
          role_template_id: template.id,
          assigned_by: currentUser?.id,
          assigned_date: new Date().toISOString()
        }, { onConflict: 'user_id,role_template_id' });

      if (linkError) throw linkError;

      // If template has no courses, we're done — just the profile link
      if (!templateCourses || templateCourses.length === 0) {
        toast.success(`Applied "${template.name}" role`);
        setApplyRoleDialogOpen(false);
        await loadData();
        return;
      }

      const spaceIds = userSpaces.map(s => s.id);

      // Get all compliance_requirements in the user's spaces
      const { data: spaceReqs, error: matchError } = await supabase
        .from('compliance_requirements')
        .select('id, name, project_id, renewal_period, course_link_id, course_link_type')
        .in('project_id', spaceIds);

      if (matchError) throw matchError;

      // Match template courses to space requirements
      // Priority: match by course_link_id first, then exact name, then auto-create
      const matchingReqs = [];
      const matchedReqIds = new Set();

      for (const tc of templateCourses) {
        let foundMatch = false;

        // Try matching by course_link_id first (most reliable)
        if (tc.course_link_id) {
          const linkMatches = (spaceReqs || []).filter(
            r => r.course_link_id === tc.course_link_id && !matchedReqIds.has(r.id)
          );
          for (const match of linkMatches) {
            matchedReqIds.add(match.id);
            matchingReqs.push(match);
            foundMatch = true;
          }
        }

        // Fall back to exact name match for any unmatched
        if (!foundMatch) {
          const nameMatches = (spaceReqs || []).filter(
            r => r.name === tc.course_name && !matchedReqIds.has(r.id)
          );
          for (const match of nameMatches) {
            matchedReqIds.add(match.id);
            matchingReqs.push(match);
            foundMatch = true;
          }
        }

        // No match found — auto-create the requirement in each of the user's spaces
        if (!foundMatch) {
          for (const spaceId of spaceIds) {
            const { data: newReq, error: createError } = await supabase
              .from('compliance_requirements')
              .insert({
                project_id: spaceId,
                name: tc.course_name,
                course_link_id: tc.course_link_id,
                course_link_type: tc.course_link_type,
                created_by: currentUser?.id,
                show_on_matrix: true
              })
              .select('id, name, project_id, renewal_period')
              .single();

            if (!createError && newReq) {
              matchingReqs.push(newReq);
            }
          }
        }
      }

      let assignedCount = 0;

      for (const req of matchingReqs) {
        // Check if assignment already exists
        const { data: existing } = await supabase
          .from('compliance_assignments')
          .select('id, assigned, role_template_ids')
          .eq('project_id', req.project_id)
          .eq('requirement_id', req.id)
          .eq('team_member_id', userId)
          .maybeSingle();

        if (existing) {
          // Assignment exists - add template ID and ensure assigned=true
          const currentTemplateIds = existing.role_template_ids || [];
          if (!currentTemplateIds.includes(template.id)) {
            currentTemplateIds.push(template.id);
          }
          await supabase
            .from('compliance_assignments')
            .update({
              assigned: true,
              role_template_ids: currentTemplateIds,
              updated_date: new Date().toISOString()
            })
            .eq('id', existing.id);
        } else {
          // Create new assignment with template ID
          let dueDate = new Date();
          switch (req.renewal_period) {
            case 'annual':
              dueDate.setFullYear(dueDate.getFullYear() + 1);
              break;
            case '3 years':
              dueDate.setFullYear(dueDate.getFullYear() + 3);
              break;
            case '5 years':
              dueDate.setFullYear(dueDate.getFullYear() + 5);
              break;
            default:
              dueDate.setMonth(dueDate.getMonth() + 12);
          }

          await supabase
            .from('compliance_assignments')
            .insert({
              project_id: req.project_id,
              requirement_id: req.id,
              team_member_id: userId,
              status: 'pending',
              due_date: dueDate.toISOString().split('T')[0],
              assigned_by: currentUser?.id,
              assigned: true,
              role_template_ids: [template.id]
            });
        }
        assignedCount++;
      }

      toast.success(`Applied "${template.name}" (${assignedCount} requirements assigned)`);
      setApplyRoleDialogOpen(false);
      await loadData();
    } catch (error) {
      console.error('Error applying role template:', error);
      toast.error('Failed to apply role template');
    } finally {
      setApplyingRoleId(null);
    }
  }, [userId, company?.id, currentUser?.id, userSpaces, loadData]);

  // Handle assignment
  const handleAssign = useCallback(async (requirement) => {
    const operationKey = `assign-${requirement.project_id}-${requirement.id}`;

    // Check if operation is already pending
    if (pendingOperationsRef.current.has(operationKey)) {
      return;
    }

    // Mark operation as pending
    pendingOperationsRef.current.set(operationKey, true);

    // Optimistic update
    setAssignedRequirements(prev => {
      const spaceId = requirement.project_id;
      const existing = prev[spaceId] || [];

      // Create a new assignment object
      const newAssignment = {
        id: `temp-${Date.now()}`,
        project_id: spaceId,
        requirement_id: requirement.id,
        team_member_id: userId,
        name: requirement.name,
        category: requirement.category,
        priority: requirement.priority,
        legal_requirement: requirement.legal_requirement,
        status: 'pending',
        assigned_date: new Date().toISOString()
      };

      return {
        ...prev,
        [spaceId]: [...existing, newAssignment]
      };
    });

    setSaving(true);

    try {
      // Calculate due date based on renewal period
      let dueDate = new Date();
      switch (requirement.renewal_period) {
        case 'annual':
          dueDate.setFullYear(dueDate.getFullYear() + 1);
          break;
        case '3 years':
          dueDate.setFullYear(dueDate.getFullYear() + 3);
          break;
        case '5 years':
          dueDate.setFullYear(dueDate.getFullYear() + 5);
          break;
        default:
          dueDate.setMonth(dueDate.getMonth() + 12); // Default 1 year
      }

      // Check if assignment already exists (might be unassigned)
      const { data: existing } = await supabase
        .from('compliance_assignments')
        .select('id')
        .eq('project_id', requirement.project_id)
        .eq('requirement_id', requirement.id)
        .eq('team_member_id', userId)
        .maybeSingle();

      let data;
      if (existing) {
        // Update existing record to assigned = true
        const { data: updated, error } = await supabase
          .from('compliance_assignments')
          .update({
            assigned: true,
            assigned_by: currentUser?.id,
            updated_date: new Date().toISOString()
          })
          .eq('id', existing.id)
          .select()
          .single();

        if (error) throw error;
        data = updated;
      } else {
        // Insert new record
        const { data: inserted, error } = await supabase
          .from('compliance_assignments')
          .insert({
            project_id: requirement.project_id,
            requirement_id: requirement.id,
            team_member_id: userId,
            status: 'pending',
            due_date: dueDate.toISOString().split('T')[0],
            assigned_by: currentUser?.id,
            assigned: true
          })
          .select()
          .single();

        if (error) throw error;
        data = inserted;
      }

      // Update optimistic state with real data
      setAssignedRequirements(prev => {
        const spaceId = requirement.project_id;
        const existingAssignments = prev[spaceId] || [];

        return {
          ...prev,
          [spaceId]: existingAssignments.map(a =>
            a.id.startsWith?.('temp-') && a.requirement_id === requirement.id
              ? { ...a, id: data.id }
              : a
          )
        };
      });

      toast.success(`Assigned ${requirement.name}`);

    } catch (error) {
      console.error('Error assigning requirement:', error);
      toast.error('Failed to assign requirement');

      // Revert optimistic update
      setAssignedRequirements(prev => {
        const spaceId = requirement.project_id;
        const existing = prev[spaceId] || [];

        return {
          ...prev,
          [spaceId]: existing.filter(a => a.requirement_id !== requirement.id)
        };
      });
    } finally {
      pendingOperationsRef.current.delete(operationKey);
      setSaving(false);
    }
  }, [userId, currentUser?.id]);

  // Handle unassignment
  const handleUnassign = useCallback(async (assignment) => {
    const operationKey = `unassign-${assignment.project_id}-${assignment.requirement_id}`;

    // Check if operation is already pending
    if (pendingOperationsRef.current.has(operationKey)) {
      return;
    }

    // Mark operation as pending
    pendingOperationsRef.current.set(operationKey, true);

    // Optimistic update
    const removedAssignment = assignment;
    setAssignedRequirements(prev => {
      const spaceId = assignment.project_id;
      const existing = prev[spaceId] || [];

      return {
        ...prev,
        [spaceId]: existing.filter(a => a.id !== assignment.id)
      };
    });

    setSaving(true);

    try {
      // Update compliance_assignment to set assigned = false
      const { error } = await supabase
        .from('compliance_assignments')
        .update({
          assigned: false,
          updated_date: new Date().toISOString()
        })
        .eq('id', assignment.id);

      if (error) throw error;

      toast.success(`Unassigned ${assignment.name}`);

    } catch (error) {
      console.error('Error unassigning requirement:', error);
      toast.error('Failed to unassign requirement');

      // Revert optimistic update
      setAssignedRequirements(prev => {
        const spaceId = removedAssignment.project_id;
        const existing = prev[spaceId] || [];

        return {
          ...prev,
          [spaceId]: [...existing, removedAssignment]
        };
      });
    } finally {
      pendingOperationsRef.current.delete(operationKey);
      setSaving(false);
    }
  }, []);

  // Toggle section collapse
  const toggleSection = useCallback((sectionKey) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  }, []);

  // Get space name by ID
  const getSpaceName = useCallback((spaceId) => {
    const space = userSpaces.find(s => s.id === spaceId);
    return space?.name || 'Unknown Space';
  }, [userSpaces]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="border rounded-lg p-4">
            <Skeleton className="h-4 w-24 mb-4" />
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
          <div className="border rounded-lg p-4">
            <Skeleton className="h-4 w-24 mb-4" />
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (userSpaces.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
        <Building className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg font-medium">No Spaces Found</p>
        <p className="text-sm mt-1">This user is not a member of any spaces</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search requirements..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={selectedSpaceFilter} onValueChange={(value) => {
          setSelectedSpaceFilter(value);
          // Update URL search params
          if (value === 'all') {
            searchParams.delete('spaceId');
          } else {
            searchParams.set('spaceId', value);
          }
          setSearchParams(searchParams, { replace: true });
        }}>
          <SelectTrigger className="w-[200px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="All Spaces" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Spaces</SelectItem>
            {userSpaces.map(space => (
              <SelectItem key={space.id} value={space.id}>
                {space.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Popover open={applyRoleDialogOpen} onOpenChange={(open) => { setApplyRoleDialogOpen(open); if (!open) setRoleSearchQuery(''); }}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 gap-1.5">
              <ShieldCheck className="h-4 w-4" />
              Apply Role
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="end">
            <div className="p-3 border-b space-y-2">
              <div>
                <div className="text-sm font-medium">Apply Role Template</div>
                <div className="text-xs text-gray-500">Assign a set of qualifications</div>
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search roles..."
                  value={roleSearchQuery}
                  onChange={e => setRoleSearchQuery(e.target.value)}
                  className="h-8 pl-8 text-sm"
                />
              </div>
            </div>
            {roleTemplates.length > 0 ? (
              <ScrollArea className="max-h-[260px]">
                {roleTemplates
                  .filter(t => !roleSearchQuery.trim() || t.name.toLowerCase().includes(roleSearchQuery.toLowerCase()))
                  .map(template => (
                  <button
                    key={template.id}
                    onClick={() => handleApplyRoleTemplate(template)}
                    disabled={!!applyingRoleId}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 transition-colors text-left border-b last:border-0 disabled:opacity-50"
                  >
                    <div
                      className="w-3 h-3 rounded-full shrink-0"
                      style={{ backgroundColor: template.color || '#6366f1' }}
                    />
                    <span className="text-sm font-medium flex-1">{template.name}</span>
                    {applyingRoleId === template.id && <Loader2 className="h-3 w-3 animate-spin" />}
                  </button>
                ))}
                {roleTemplates.filter(t => !roleSearchQuery.trim() || t.name.toLowerCase().includes(roleSearchQuery.toLowerCase())).length === 0 && (
                  <div className="px-3 py-4 text-center text-sm text-gray-500">
                    No roles matching "{roleSearchQuery}"
                  </div>
                )}
              </ScrollArea>
            ) : (
              <div className="px-3 py-4 text-center text-sm text-gray-500">
                No role templates yet
              </div>
            )}
            {hasEditPermission && (
              <div className="p-2 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start gap-2 text-sm"
                  onClick={() => {
                    setApplyRoleDialogOpen(false);
                    setCreateRoleDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4" />
                  Create new role
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
        {saving && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Saving...
          </div>
        )}
      </div>

      {/* Applied Role Templates */}
      {userAppliedTemplateIds.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Roles:</span>
          {userAppliedTemplateIds.map(templateId => {
            const tmpl = roleTemplateMap.get(templateId);
            if (!tmpl) return null;
            const badge = (
              <Badge
                variant="outline"
                className={cn(
                  "text-[11px] py-0.5 px-2",
                  tmpl.job_profile && "cursor-pointer"
                )}
                style={{
                  backgroundColor: (tmpl.color || '#6366f1') + '15',
                  borderColor: (tmpl.color || '#6366f1') + '40',
                  color: tmpl.color || '#6366f1'
                }}
              >
                {tmpl.name}
              </Badge>
            );
            if (tmpl.job_profile) {
              return (
                <Popover key={templateId}>
                  <PopoverTrigger asChild>
                    {badge}
                  </PopoverTrigger>
                  <PopoverContent className="w-80 max-h-64 overflow-y-auto" align="start">
                    <div className="space-y-2">
                      <div className="font-medium text-sm">{tmpl.name} — Job Profile</div>
                      <div
                        className="prose prose-sm text-xs text-muted-foreground"
                        dangerouslySetInnerHTML={{ __html: tmpl.job_profile }}
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              );
            }
            return <span key={templateId}>{badge}</span>;
          })}
        </div>
      )}

      {/* Split Panel UI */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* LEFT PANEL: Assigned Requirements */}
        <div className="border rounded-lg flex flex-col min-h-[400px] max-h-[600px] overflow-hidden">
          <div className="px-3 py-2 border-b bg-gray-50/50 shrink-0 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GraduationCap className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium">Assigned</span>
            </div>
            <Badge variant="secondary" className="text-xs">
              {assignedCount}
            </Badge>
          </div>

          <ScrollArea className="flex-1 min-h-0 rounded-b-lg">
            {assignedCount === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400 px-4">
                <FileText className="h-8 w-8 mb-2 opacity-50" />
                <p className="text-sm">No requirements assigned</p>
                <p className="text-xs mt-1">Click + on requirements to assign</p>
              </div>
            ) : (
              Object.keys(filteredAssigned).map(spaceId => {
                const requirements = filteredAssigned[spaceId] || [];
                if (requirements.length === 0) return null;

                return (
                  <SpaceSection
                    key={spaceId}
                    spaceName={getSpaceName(spaceId)}
                    count={requirements.length}
                    requirements={requirements}
                    isCollapsed={collapsedSections[`assigned-${spaceId}`]}
                    onToggle={() => toggleSection(`assigned-${spaceId}`)}
                  >
                    {requirements.map(req => (
                      <AssignedRequirementRow
                        key={req.id}
                        requirement={req}
                        onUnassign={handleUnassign}
                        onRequestQuote={handleRequestQuote}
                        onViewCertificate={handleViewCertificate}
                        disabled={saving}
                        roleTemplateMap={roleTemplateMap}
                      />
                    ))}
                  </SpaceSection>
                );
              })
            )}
          </ScrollArea>
        </div>

        {/* RIGHT PANEL: Available Requirements */}
        <div className="border rounded-lg flex flex-col min-h-[400px] max-h-[600px] overflow-hidden">
          <div className="px-3 py-2 border-b bg-gray-50/50 shrink-0 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium">Available</span>
            </div>
            <Badge variant="secondary" className="text-xs">
              {availableCount}
            </Badge>
          </div>

          <ScrollArea className="flex-1 min-h-0 rounded-b-lg">
            {availableCount === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400 px-4">
                <CheckCircle className="h-8 w-8 mb-2 opacity-50" />
                <p className="text-sm font-medium">All requirements assigned</p>
                <p className="text-xs mt-1">No more requirements available</p>
              </div>
            ) : (
              Object.keys(filteredAvailable).map(spaceId => {
                const requirements = filteredAvailable[spaceId] || [];
                if (requirements.length === 0) return null;

                return (
                  <SpaceSection
                    key={spaceId}
                    spaceName={getSpaceName(spaceId)}
                    count={requirements.length}
                    isCollapsed={collapsedSections[`available-${spaceId}`]}
                    onToggle={() => toggleSection(`available-${spaceId}`)}
                  >
                    {requirements.map(req => (
                      <AvailableRequirementRow
                        key={req.id}
                        requirement={req}
                        onAssign={handleAssign}
                        disabled={saving}
                      />
                    ))}
                  </SpaceSection>
                );
              })
            )}
          </ScrollArea>
        </div>
      </div>

      {/* Help text */}
      <p className="text-xs text-gray-500 text-center">
        Use + to assign and - to unassign requirements. Changes are saved automatically.
      </p>

      {/* Certificate Detail Sheet */}
      <CertificateDetailSheet
        requirement={certDetailRequirement}
        open={certDetailOpen}
        onOpenChange={(open) => {
          setCertDetailOpen(open);
          if (!open) setCertDetailRequirement(null);
        }}
      />

      {/* Profile Quick Quote Popup */}
      <ProfileQuickQuotePopup
        isOpen={quickQuoteOpen}
        onClose={() => {
          setQuickQuoteOpen(false);
          setQuickQuoteRequirement(null);
        }}
        userId={userId}
        userName={userProfile ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() : 'Unknown'}
        userAvatar={userProfile?.avatar_url}
        requirementId={quickQuoteRequirement?.requirement_id}
        requirementName={quickQuoteRequirement?.name}
        projectId={quickQuoteRequirement?.project_id}
        onSuccess={loadData}
      />

      {/* Create Role Template Dialog */}
      <RoleTemplateDialog
        isOpen={createRoleDialogOpen}
        onClose={() => setCreateRoleDialogOpen(false)}
        onSuccess={loadData}
      />
    </div>
  );
}

export default ProfileQualificationsManager;
